import { Head, Link, router } from '@inertiajs/react'
import { useState } from 'react'
import AdminLayout from '../../components/layout/AdminLayout'

export default function PropertiesIndex({ properties, filters, stats }) {
  const [selectedStatus, setSelectedStatus] = useState(filters.status || '')
  const [selectedPropertyType, setSelectedPropertyType] = useState(filters.propertyType || '')
  const [selectedTransactionType, setSelectedTransactionType] = useState(filters.transactionType || '')

  const applyFilters = (newFilters) => {
    router.get('/properties', {
      ...filters,
      ...newFilters,
      page: 1
    }, {
      preserveState: true,
      preserveScroll: true
    })
  }

  const clearFilters = () => {
    setSelectedStatus('')
    setSelectedPropertyType('')
    setSelectedTransactionType('')
    router.get('/properties', {}, { preserveState: true })
  }

  const hasActiveFilters = filters.status || filters.propertyType || filters.transactionType

  const formatPrice = (price) => {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 0
    }).format(price)
  }

  const translatePropertyType = (type) => {
    const translations = {
      'apartment': 'Appartement',
      'house': 'Maison',
      'land': 'Terrain',
      'commercial': 'Commercial',
      'parking': 'Parking'
    }
    return translations[type] || type
  }

  const translateTransactionType = (type) => {
    const translations = {
      'sale': 'Vente',
      'rental': 'Location'
    }
    return translations[type] || type
  }

  const translateStatus = (status) => {
    const translations = {
      'available': 'Disponible',
      'under_offer': 'Sous offre',
      'sold': 'Vendu',
      'rented': 'Loué',
      'withdrawn': 'Retiré'
    }
    return translations[status] || status
  }

  return (
    <AdminLayout>
      <Head title="Propriétés" />

      <div>
        <div className="mx-auto max-w-7xl">
          {/* Header */}
          <div className="mb-8 flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Propriétés</h1>
              <p className="mt-2 text-sm text-gray-600">
                Gérez votre catalogue de biens immobiliers
              </p>
            </div>
            <Link
              href="/properties/new"
              className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              Nouvelle propriété
            </Link>
          </div>

          {/* Stats Cards */}
          <div className="mb-8 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-5">
            <StatsCard title="Total" value={stats.total} color="blue" />
            <StatsCard title="Disponible" value={stats.available} color="green" />
            <StatsCard title="En vente" value={stats.forSale} color="indigo" />
            <StatsCard title="En location" value={stats.forRental} color="purple" />
            <StatsCard title="Sous offre" value={stats.underOffer} color="yellow" />
          </div>

          {/* Filters */}
          <div className="mb-6 bg-white rounded-lg shadow-sm p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Filtres</h2>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
              {/* Status Filter */}
              <div>
                <label htmlFor="status" className="block text-sm font-medium text-gray-700 mb-2">
                  Statut
                </label>
                <select
                  id="status"
                  value={selectedStatus}
                  onChange={(e) => {
                    setSelectedStatus(e.target.value)
                    applyFilters({ status: e.target.value })
                  }}
                  className="w-full rounded-lg border border-gray-300 focus:border-primary-500 focus:ring-primary-500 focus:ring-2 focus:ring-offset-1 focus:outline-none"
                >
                  <option value="">Tous les statuts</option>
                  <option value="available">Disponible</option>
                  <option value="under_offer">Sous offre</option>
                  <option value="sold">Vendu</option>
                  <option value="rented">Loué</option>
                  <option value="withdrawn">Retiré</option>
                </select>
              </div>

              {/* Property Type Filter */}
              <div>
                <label htmlFor="property_type" className="block text-sm font-medium text-gray-700 mb-2">
                  Type de bien
                </label>
                <select
                  id="property_type"
                  value={selectedPropertyType}
                  onChange={(e) => {
                    setSelectedPropertyType(e.target.value)
                    applyFilters({ property_type: e.target.value })
                  }}
                  className="w-full rounded-lg border border-gray-300 focus:border-primary-500 focus:ring-primary-500 focus:ring-2 focus:ring-offset-1 focus:outline-none"
                >
                  <option value="">Tous les types</option>
                  <option value="apartment">Appartement</option>
                  <option value="house">Maison</option>
                  <option value="land">Terrain</option>
                  <option value="commercial">Commercial</option>
                  <option value="parking">Parking</option>
                </select>
              </div>

              {/* Transaction Type Filter */}
              <div>
                <label htmlFor="transaction_type" className="block text-sm font-medium text-gray-700 mb-2">
                  Transaction
                </label>
                <select
                  id="transaction_type"
                  value={selectedTransactionType}
                  onChange={(e) => {
                    setSelectedTransactionType(e.target.value)
                    applyFilters({ transaction_type: e.target.value })
                  }}
                  className="w-full rounded-lg border border-gray-300 focus:border-primary-500 focus:ring-primary-500 focus:ring-2 focus:ring-offset-1 focus:outline-none"
                >
                  <option value="">Tous</option>
                  <option value="sale">Vente</option>
                  <option value="rental">Location</option>
                </select>
              </div>

              {/* Clear Filters Button */}
              {hasActiveFilters && (
                <div className="sm:col-span-3">
                  <button
                    type="button"
                    onClick={clearFilters}
                    className="px-4 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 transition-colors"
                  >
                    Réinitialiser les filtres
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Properties Table */}
          <div className="bg-white rounded-lg shadow-sm overflow-hidden">
            <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Bien
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Type
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Prix
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Détails
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Statut
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {properties.length === 0 ? (
                  <tr>
                    <td colSpan="6" className="px-6 py-12 text-center text-gray-500">
                      {hasActiveFilters ? 'Aucune propriété trouvée avec ces filtres' : 'Aucune propriété pour le moment'}
                    </td>
                  </tr>
                ) : (
                  properties.map((property) => (
                    <tr
                      key={property.id}
                      onClick={() => router.visit(`/properties/${property.id}`)}
                      className="hover:bg-blue-50 cursor-pointer transition-colors"
                    >
                      <td className="px-6 py-4">
                        <div>
                          <div className="text-sm font-medium text-gray-900">
                            {property.title || property.address}
                          </div>
                          <div className="text-sm text-gray-500">
                            {property.city} {property.zipCode}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="inline-flex px-2 text-xs font-semibold leading-5 text-gray-800 bg-gray-100 rounded-full">
                          {translatePropertyType(property.propertyType)}
                        </span>
                        <div className="text-xs text-gray-500 mt-1">
                          {translateTransactionType(property.transactionType)}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-semibold text-gray-900">
                          {formatPrice(property.price)}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {property.surface && <div>{property.surface} m²</div>}
                        {property.rooms && <div>{property.rooms} pièces</div>}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <StatusBadge status={property.status} />
                      </td>
                      <td
                        className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <Link
                          href={`/properties/${property.id}`}
                          className="inline-flex items-center gap-1 text-blue-600 hover:text-blue-800 font-medium transition-colors"
                        >
                          <span>Voir</span>
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                          </svg>
                        </Link>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
            </div>
          </div>
        </div>
      </div>
    </AdminLayout>
  )
}

// StatsCard Component
function StatsCard({ title, value, color = 'blue' }) {
  const colorClasses = {
    blue: 'bg-blue-50 text-blue-700',
    green: 'bg-green-50 text-green-700',
    yellow: 'bg-yellow-50 text-yellow-700',
    indigo: 'bg-indigo-50 text-indigo-700',
    purple: 'bg-purple-50 text-purple-700'
  }

  return (
    <div className="overflow-hidden bg-white rounded-lg shadow">
      <div className="px-4 py-5 sm:p-6">
        <dt className="text-sm font-medium text-gray-500 truncate">{title}</dt>
        <dd className={`mt-1 text-3xl font-semibold ${colorClasses[color]}`}>{value}</dd>
      </div>
    </div>
  )
}

// StatusBadge Component
function StatusBadge({ status }) {
  const statusConfig = {
    available: { label: 'Disponible', color: 'bg-green-100 text-green-800' },
    under_offer: { label: 'Sous offre', color: 'bg-yellow-100 text-yellow-800' },
    sold: { label: 'Vendu', color: 'bg-blue-100 text-blue-800' },
    rented: { label: 'Loué', color: 'bg-purple-100 text-purple-800' },
    withdrawn: { label: 'Retiré', color: 'bg-red-100 text-red-800' }
  }

  const config = statusConfig[status] || statusConfig.available

  return (
    <span className={`inline-flex px-2 text-xs font-semibold leading-5 rounded-full ${config.color}`}>
      {config.label}
    </span>
  )
}
